import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { EnrollmentLanding } from 'features/enrollment';
import { Layout } from 'features/layout/Layout';
import { EmptyStatePage } from 'shared/components/EmptyStatePage';
import { assertExhaustiveMatch } from 'shared/utils/typeUtils';
import type { DbParticipation } from '@hub/common';

// The bare outlet — no nav chrome. Renders the questionnaire and other
// standalone routes for any participation, focused or active. Paints an
// opaque default background so the body's stripes stay out of the form.
// The height must stay definite: the questionnaire sizes itself against it.
const FocusedOutlet = () => (
  <Box component="main" sx={{ height: '100%', bgcolor: 'background.default' }}>
    <Outlet />
  </Box>
);

// Minimal resting state for terminal enrollment outcomes.
const TerminalShell = ({
  titleKey,
  subtitleKey,
}: {
  titleKey: string;
  subtitleKey: string;
}) => {
  const { t } = useTranslation();

  return <EmptyStatePage title={t(titleKey)} subtitle={t(subtitleKey)} />;
};

type ParticipationShellProps = {
  participation: DbParticipation;
  withLayout: boolean;
  standalone: boolean;
  isHomeRoute: boolean;
  userId: string;
};

export const ParticipationShell = ({
  participation,
  withLayout,
  standalone,
  isHomeRoute,
  userId,
}: ParticipationShellProps) => {
  switch (participation.status) {
    case 'active':
      return withLayout && !standalone ? <Layout /> : <FocusedOutlet />;

    // Terminal outcome on the home route, but keep the bare outlet on other
    // routes so a prescreener whose submission flips the status to
    // `pre-screen-failed` can still finish its post-submission display (the
    // post-questionnaire phase) before landing on the terminal shell at home.
    // Without this, the status flip mid-submit unmounts the active task page.
    case 'pre-screen-failed':
      return isHomeRoute ? (
        <TerminalShell
          titleKey="PreScreeningCompleteTitle"
          subtitleKey="PreScreeningCompleteSubtitle"
        />
      ) : (
        <FocusedOutlet />
      );

    // Pre-active and enrollment-complete phases never render the real home: on
    // the home route they land on the enrollment landing (progress + the phase
    // questionnaire CTA); the questionnaire and other focused routes render
    // through the bare outlet.
    case 'pre-screening':
    case 'consenting':
    case 'consented':
    case 'pre-screened':
      return isHomeRoute ? (
        <EnrollmentLanding userId={userId} participationId={participation.id} />
      ) : (
        <FocusedOutlet />
      );

    // Terminal outcome on the home route; bare outlet elsewhere (same reasoning
    // as pre-screen-failed).
    case 'consent-declined':
      return isHomeRoute ? (
        <TerminalShell
          titleKey="ConsentDeclinedTitle"
          subtitleKey="ConsentDeclinedSubtitle"
        />
      ) : (
        <FocusedOutlet />
      );

    default:
      return assertExhaustiveMatch(
        '[ASSERT FAILED] ParticipationShell: unhandled participation status',
        participation.status
      );
  }
};
